// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2025 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import isApiError from "@insite/client-framework/Common/isApiError";
import {
    createHandlerChainRunner,
    Handler,
    HasOnError,
    HasOnSuccess,
    markSkipOnCompleteIfOnErrorIsSet,
    markSkipOnCompleteIfOnSuccessIsSet,
} from "@insite/client-framework/HandlerCreator";
import { updateCustomerPartNumber } from "@steiner/App/Services/CustomerPartNumbersServices";

interface Props {
    result: {
        errorMessage?: string;
    };
}

export interface UpdateCustomerPartNumberParameter {
    customerPartNumber?: string;
    productNumber?: string;
}

type HandlerType = Handler<UpdateCustomerPartNumberParameter & HasOnSuccess & HasOnError<string>, Props>;

export const DispatchUpdatingCustomerPartNumbers: HandlerType = props => {
    props.dispatch({
        type: "xCustomerPartNumbers/setLoading",
        isLoading: true,
    } as any);
};

export const RequestDataFromApi: HandlerType = async props => {
    props.result = {};
    try {
        await updateCustomerPartNumber({
            customerPartNumber: props.parameter.customerPartNumber,
            productNumber: props.parameter.productNumber,
        });
    } catch (error) {
        if (isApiError(error) && error.status === 400) {
            props.result.errorMessage = error.errorJson.message;
            return;
        }
        throw error;
    }
};

export const DispatchUpdateCustomerPartNumber: HandlerType = props => {
    if (!props.result.errorMessage) {
        props.dispatch({
            type: "xCustomerPartNumbers/UpdateCustomerPartNumber",
            customerPartNumber: props.parameter.customerPartNumber,
            productNumber: props.parameter.productNumber,
        } as any);
    }
};

export const ExecuteOnSuccessCallback: HandlerType = props => {
    if (!props.result?.errorMessage && props.parameter.onSuccess) {
        markSkipOnCompleteIfOnSuccessIsSet(props);
        props.parameter.onSuccess();
    }
};

export const ExecuteOnErrorCallback: HandlerType = props => {
    if (props.result?.errorMessage) {
        markSkipOnCompleteIfOnErrorIsSet(props);
        props.parameter.onError?.(props.result.errorMessage);
    }
};

export const chain = [
    DispatchUpdatingCustomerPartNumbers,
    RequestDataFromApi,
    DispatchUpdateCustomerPartNumber,
    ExecuteOnSuccessCallback,
    ExecuteOnErrorCallback,
];

const updateCustomerPartNumberHandler = createHandlerChainRunner(chain, "updateCustomerPartNumberHandler");

export default updateCustomerPartNumberHandler;
